import {
  perspectiveEditorFormattingOptions$,
  perspectiveEditorGroupingOptions$,
} from 'streams/perspectiveEditorData';
import { combineLatest, map, Observable } from 'rxjs';
import { dispatchAction } from '@ardoq/rxbeach';
import { showViewSidePanel } from 'views/mainApp/viewSidePanel/actions';
import { ViewSidePanelContentIds } from 'views/mainApp/viewSidePanel/types';
import { trackEventV2 } from 'tracking/tracking';
import { AppliedSettingsData } from '../appliedSettings/types';
import { loadedState$ } from 'loadedState/loadedState$';
import {
  getAppliedLabelFormatting,
  getAppliedConditionalFormatting,
  getAppliedGroupingRuleDescriptions,
  getContextComponentTypeSettings,
  getComponentsAndReferencesTypeSettings,
  getFilterSettings,
  getViewpointData,
} from './utils';
import { noop } from 'lodash';
import { metaModelAsScopeData$ } from 'viewpointBuilder/metaModel/loadMetaModelAsScopeData$';
import { Features, featureOperations } from '@ardoq/features';
import { featureContext$ } from 'streams/featureContext$';
import { openSidebarPanel } from 'canvas/sidebarPanel/actions';
import { SidebarPanelContentIds } from 'canvas/sidebarPanel/types';
import { i18nLocale$ } from 'streams/i18nLocale$';

export const getDefaultAppliedSettingsData = (): AppliedSettingsData => ({
  contextComponentTypeSettings: {},
  labelSettings: {
    onEditClick: noop,
    customLabelsCount: 0,
    componentFormattingLabel: undefined,
    referenceFormattingLabel: undefined,
  },
  formattingSettings: {
    onEditClick: noop,
    appliedRulesCount: 0,
    ruleDescriptionsByEntity: {},
  },
  groupingSettings: {
    onEditClick: noop,
    appliedGroupingRuleDescriptions: [],
  },
  componentAndReferenceTypesSettings: {
    onEditClick: noop,
    componentTypesCount: 0,
    referenceTypesCount: 0,
  },
  filterSettings: {
    onEditClick: noop,
    appliedFiltersCount: 0,
  },
  viewpointData: null,
});

const onLabelsRowEditClick = (isCanvasPrototype: boolean) => {
  if (isCanvasPrototype) {
    dispatchAction(
      openSidebarPanel(SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_LABELS)
    );
  } else {
    dispatchAction(
      showViewSidePanel(ViewSidePanelContentIds.PERSPECTIVES_LABELS)
    );
  }
  trackEventV2({ event: 'Save As Viewpoint0 Edit labels clicked' });
};

const onFormattingRowEditClick = (isCanvasPrototype: boolean) => {
  if (isCanvasPrototype) {
    dispatchAction(
      openSidebarPanel(
        SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_CONDITIONAL_FORMAT
      )
    );
  } else {
    dispatchAction(
      showViewSidePanel(ViewSidePanelContentIds.PERSPECTIVES_CONDITIONAL_FORMAT)
    );
  }
  trackEventV2({ event: 'Save As Viewpoint0 Edit formatting clicked' });
};

const onGroupingRowEditClick = (isCanvasPrototype: boolean) => {
  if (isCanvasPrototype) {
    dispatchAction(
      openSidebarPanel(SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_GROUPS)
    );
  } else {
    dispatchAction(
      showViewSidePanel(ViewSidePanelContentIds.PERSPECTIVES_GROUPS)
    );
  }
  trackEventV2({ event: 'Save As Viewpoint0 Edit grouping clicked' });
};

export const appliedSettingsData$: Observable<AppliedSettingsData> =
  combineLatest([
    perspectiveEditorFormattingOptions$,
    perspectiveEditorGroupingOptions$,
    loadedState$,
    metaModelAsScopeData$,
    featureContext$,
    i18nLocale$,
  ]).pipe(
    map(
      ([
        { appliedLabelFormatting, appliedShowReferenceType },
        groupOptions,
        { loadedStates },
        metaModelAsScopeData,
        featureContext,
        i18nLocale,
      ]): AppliedSettingsData => {
        const isCanvasPrototype = featureOperations.hasFeature(
          featureContext,
          Features.CANVAS_PROTOTYPE
        );
        const componentTypes =
          metaModelAsScopeData.status === 'DATA_LOADED'
            ? metaModelAsScopeData.componentTypes
            : [];

        const appliedLabelSettings = getAppliedLabelFormatting({
          appliedLabelFormatting,
          appliedShowReferenceType,
          i18nLocale,
        });
        return {
          componentAndReferenceTypesSettings:
            getComponentsAndReferencesTypeSettings(loadedStates),
          contextComponentTypeSettings: getContextComponentTypeSettings(
            loadedStates,
            componentTypes
          ),
          filterSettings: getFilterSettings(loadedStates),
          labelSettings: {
            onEditClick: () => onLabelsRowEditClick(isCanvasPrototype),
            ...appliedLabelSettings,
          },
          formattingSettings: {
            onEditClick: () => onFormattingRowEditClick(isCanvasPrototype),
            ...getAppliedConditionalFormatting(i18nLocale),
          },
          groupingSettings: {
            onEditClick: () => onGroupingRowEditClick(isCanvasPrototype),
            appliedGroupingRuleDescriptions: getAppliedGroupingRuleDescriptions(
              groupOptions,
              i18nLocale
            ),
          },
          viewpointData: getViewpointData(loadedStates),
        };
      }
    )
  );
